import { useEffect, useState } from 'react';
import { GetHelpCenterArticle } from '@/apis/helpCenterAPI';
import { useToken } from '@/common/helper';
import type { HelpArticle } from '../../types';

export const useHelpArticle = (articleId: string) => {
  const { token } = useToken();
  const [data, setData] = useState<HelpArticle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Backend's reason when the article can't be shown (hidden for the role, not found).
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Backend outcome (opened, hidden, not found); sent with help_article_link_open.
  const [outcome, setOutcome] = useState<string | null>(null);

  useEffect(() => {
    // Ignores a response that lands after the user opened another article.
    let cancelled = false;
    setLoading(true);
    setError(false);
    setErrorMessage(null);
    setOutcome(null);

    GetHelpCenterArticle(token, articleId)
      .then((res) => {
        if (cancelled) return;
        const result = res?.data?.data?.data;
        setOutcome(result?.outcome ?? null);
        if (result?.outcome === 'opened' && result.article) {
          setData(result.article);
        } else {
          // Hidden or not found: show the backend's message for it.
          const message = res?.data?.data?.message;
          setErrorMessage(typeof message === 'string' ? message : null);
          setData(null);
          setError(true);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, articleId]);

  return { data, loading, error, errorMessage, outcome };
};

export default useHelpArticle;
